/**
 * <DateField> — Gregorian date-picker with RTL-correct calendar mirroring.
 *
 * Pitfall H12 (RTL calendar mirroring): react-day-picker v9's `dir` prop
 * correctly mirrors the grid for Arabic — first column becomes Saturday on
 * the right in RTL vs. Sunday on the left in LTR (locale-aware starts handled
 * by rdp itself). This wrapper:
 *
 *   1. Resolves `dir` in priority order:
 *         explicit `dir` prop > `document.documentElement.dir` > "ltr".
 *   2. Exposes a day-1 inline calendar rendering (`inline`) for the default
 *      Storybook stories + a Popover-anchored trigger (`<Input>` button) for
 *      the composed usage. Popover layer reuses the 04b `<Popover>` primitive.
 *   3. Styles day cells via semantic tokens only — no hex, no `left-*`/`ml-*`.
 *
 * Under `<html lang="ar">` the calendar takes rdp's Arabic locale (month and
 * weekday names, button labels, Saturday first); English otherwise. A
 * consumer's own `locale` prop still wins.
 */
import * as React from "react";
import { DayPicker } from "react-day-picker";
import type { DayPickerProps } from "react-day-picker";
import { ar } from "react-day-picker/locale";
import { X } from "lucide-react";
import { cn } from "../../utils";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { PopoverRoot, PopoverTrigger, PopoverContent } from "../Popover";
import { HijriDayButton } from "./HijriDayButton";
import "react-day-picker/style.css";

/**
 * Resolve the reading direction. Explicit consumer prop wins; otherwise we
 * read `<html dir>` at mount so SSR + initial render match. We intentionally
 * re-read on every render (cheap) so a mid-session direction toggle (Storybook
 * decorator, preview mode, locale switcher) re-mirrors the calendar.
 */
function useResolvedDir(explicit?: "ltr" | "rtl"): "ltr" | "rtl" {
  const [resolved, setResolved] = React.useState<"ltr" | "rtl">(explicit ?? "ltr");

  React.useEffect(() => {
    if (explicit !== undefined) {
      setResolved(explicit);
      return;
    }
    if (typeof document !== "undefined") {
      const htmlDir = document.documentElement.getAttribute("dir");
      setResolved(htmlDir === "rtl" ? "rtl" : "ltr");
    }
  }, [explicit]);

  return resolved;
}

export interface DateFieldProps extends Omit<
  DayPickerProps,
  "mode" | "selected" | "onSelect" | "dir"
> {
  /**
   * Current selected date (controlled). Undefined means no selection.
   */
  value?: Date;
  /**
   * Change handler — fires with the user's selection or `undefined` when the
   * date is cleared.
   */
  onChange?: (date: Date | undefined) => void;
  /**
   * Explicit reading direction. Omit to read `<html dir>`. Provide explicitly
   * in Storybook stories to force a specific mirroring without touching the
   * document.
   */
  dir?: "ltr" | "rtl";
  /**
   * Placeholder shown in the trigger Input when nothing is selected.
   * Defaults to `YYYY-MM-DD` — ISO-ish form reads LTR in any locale.
   */
  placeholder?: string;
  /**
   * Trigger input's accessible label — required if the field is not visually
   * labeled elsewhere (consumer's responsibility to ensure one is present).
   */
  "aria-label"?: string;
  /** Set by `<Field>` on its child; lands on the trigger input, as `id` does. */
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  /**
   * Render the calendar inline (no Popover, no trigger). Useful for day-view
   * pickers embedded in a page layout and for Storybook's default story.
   */
  inline?: boolean;
  /** Mark the field as invalid — delegates to Input's `invalid` prop. */
  invalid?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  /** Input name for native form submission. */
  name?: string;
  /** Consumer className forwarded to the calendar root. */
  className?: string;
  /** Consumer className forwarded to the trigger Input (non-inline mode). */
  triggerClassName?: string;
  /**
   * When `true`, suppresses the Hijri secondary label per day cell and
   * falls back to rdp's default Gregorian-only DayButton. Defaults to
   * `false` — the Hijri overlay is ON out of the box per DS-R-08 so every
   * date surface in HR Admin shows both calendars without opt-in. Consumers
   * for non-MENA edge surfaces can opt out explicitly.
   */
  hideHijri?: boolean;
  /**
   * Caption layout for the calendar header. Defaults to `"dropdown"` so
   * users can jump years/months instead of paginating month-by-month — a
   * must for date-of-birth + hire-date inputs that reach back decades. Pass
   * `"label"` for the rdp default text caption.
   */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
  /**
   * Show a trailing clear affordance once a date is picked — the same X-button
   * convention the entity comboboxes use. Off by default: on a required field
   * an empty date is not a state the form can be in. Clearing calls
   * `onChange(undefined)`.
   */
  clearable?: boolean;
  /**
   * Accessible name for the clear button, e.g. "Clear date of birth". Required
   * alongside `clearable` — this primitive carries no i18n of its own.
   */
  clearLabel?: string;
}

function formatIso(date: Date | undefined): string {
  if (!date) return "";
  const yyyy = date.getFullYear().toString().padStart(4, "0");
  const mm = (date.getMonth() + 1).toString().padStart(2, "0");
  const dd = date.getDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * A typed date — `YYYY-MM-DD` (also `/` or `.` separators) — as a local Date,
 * or undefined while the text is not a complete real calendar date. The
 * round-trip check rejects 2026-02-30 and the like.
 */
export function parseTypedDate(text: string): Date | undefined {
  const m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text.trim());
  if (m === null) return undefined;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
    ? date
    : undefined;
}

/**
 * Shared calendar pane — used by both `inline` and Popover modes so the
 * day-cell styling stays identical across presentations.
 */
function CalendarPane({
  value,
  onChange,
  dir,
  className,
  hideHijri = false,
  captionLayout = "dropdown",
  ...rest
}: {
  value?: Date | undefined;
  onChange?: ((d: Date | undefined) => void) | undefined;
  dir: "ltr" | "rtl";
  className?: string | undefined;
  hideHijri?: boolean;
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
} & Omit<DayPickerProps, "mode" | "selected" | "onSelect" | "dir">) {
  // Merge consumer-supplied `components` (rdp v9) with our HijriDayButton
  // override when hideHijri is false (the default). This keeps any other
  // component overrides the consumer passes intact while adding the Hijri
  // secondary label to day cells. Consumers explicitly opting out via
  // `hideHijri={true}` get rdp's default Gregorian-only DayButton.
  const { components: consumerComponents, ...otherRest } = rest;
  const mergedComponents = hideHijri
    ? consumerComponents
    : { ...consumerComponents, DayButton: HijriDayButton };

  // rdp v9 discriminates its props union by `mode`; we commit to `single`
  // (the 80% HR-SaaS case). Cast to the single-mode prop shape so the
  // exactOptionalPropertyTypes-narrow `required: true` branch is excluded.
  // captionLayout="dropdown" defaults startMonth to 100 years ago and
  // endMonth to end-of-current-year per rdp v9. We widen endMonth to +10
  // years so future-dated fields (contract end, expiry) work without
  // consumer config; consumers can still override via prop.
  const now = new Date();
  const defaultStartMonth = new Date(now.getFullYear() - 100, 0, 1);
  const defaultEndMonth = new Date(now.getFullYear() + 10, 11, 1);

  // Month, weekday and button names in the UI language, which @linkhr/i18n
  // keeps on <html lang>; read when the calendar opens, so a switch applies.
  const arabic = typeof document !== "undefined" && document.documentElement.lang.startsWith("ar");

  const dpProps = {
    mode: "single" as const,
    dir,
    captionLayout,
    ...(arabic ? { locale: ar } : {}),
    startMonth: otherRest.startMonth ?? defaultStartMonth,
    endMonth: otherRest.endMonth ?? defaultEndMonth,
    ...(value !== undefined ? { selected: value } : {}),
    // rdp v9 opens on today unless told otherwise — a picked (or typed) date
    // in another month was invisible on re-open.
    ...(value !== undefined ? { defaultMonth: value } : {}),
    onSelect: (d: Date | undefined) => onChange?.(d),
    ...(mergedComponents !== undefined ? { components: mergedComponents } : {}),
    ...otherRest,
  } as DayPickerProps;
  const mergedClass = cn(
    // Tokens-only shell per UI-SPEC (no hex, no physical sides).
    "bg-surface-elevated text-default",
    "rounded-md p-md",
    // rdp owns its own grid; we scope a few overrides below to ensure
    // day cells use semantic tokens + logical properties.
    "[&_.rdp-day]:rounded-md",
    "[&_.rdp-day_selected]:bg-primary [&_.rdp-day_selected]:text-on-primary",
    "[&_.rdp-day_today]:bg-primary-soft",
    "[&_.rdp-day:hover]:bg-surface-sunken",
    "[&_.rdp-day_disabled]:opacity-50",
    "[&_.rdp-head_cell]:text-muted [&_.rdp-head_cell]:text-label",
    // Dropdown caption styling — rdp v9 renders native <select>s for
    // year/month jumps when captionLayout="dropdown". Bring them onto the
    // app surface tokens so the picker doesn't look unstyled in dark mode.
    "[&_.rdp-dropdowns]:flex [&_.rdp-dropdowns]:items-center [&_.rdp-dropdowns]:gap-xs",
    "[&_.rdp-dropdown_root]:relative",
    "[&_select.rdp-dropdown]:appearance-none [&_select.rdp-dropdown]:bg-surface-elevated",
    "[&_select.rdp-dropdown]:text-default [&_select.rdp-dropdown]:text-body",
    "[&_select.rdp-dropdown]:border [&_select.rdp-dropdown]:border-subtle",
    "[&_select.rdp-dropdown]:rounded-md [&_select.rdp-dropdown]:px-sm [&_select.rdp-dropdown]:py-xs",
    "[&_select.rdp-dropdown]:cursor-pointer",
    "[&_select.rdp-dropdown:focus-visible]:outline-none",
    "[&_select.rdp-dropdown:focus-visible]:ring-2 [&_select.rdp-dropdown:focus-visible]:ring-primary",
    className,
  );
  return <DayPicker {...dpProps} className={mergedClass} />;
}

export const DateField = React.forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  {
    value,
    onChange,
    dir: dirProp,
    placeholder = "YYYY-MM-DD",
    inline = false,
    invalid,
    disabled,
    name,
    className,
    triggerClassName,
    hideHijri = false,
    captionLayout = "dropdown",
    clearable = false,
    clearLabel,
    ...rest
  },
  ref,
) {
  const dir = useResolvedDir(dirProp);
  const ariaLabel = rest["aria-label"];
  // Strip aria-label out of the rdp-forwarded rest so it doesn't double-apply.
  const { ["aria-label"]: _aria, ...dpRest } = rest;
  // A label's `for`, a hint and an error state name the typed input, not the calendar.
  const { id, "aria-describedby": describedBy, "aria-invalid": ariaInvalid, ...paneRest } = dpRest;
  const isInvalid = invalid === true || ariaInvalid === true || ariaInvalid === "true";
  // Hook must be at the top level — call it unconditionally even in inline
  // mode so React's order-of-hooks invariant holds across renders.
  const [open, setOpen] = React.useState(false);
  // The trigger is typeable: the text is local while being edited and re-syncs
  // from `value` whenever the picked date changes (calendar, clear, parent).
  const [text, setText] = React.useState(() => formatIso(value));
  const iso = formatIso(value);
  // Leave the text alone while it already spells the picked date (a lenient
  // "2027-6-3" mid-typing) — rewriting it under the caret garbled the input.
  React.useEffect(() => {
    setText((cur) => {
      const parsed = parseTypedDate(cur);
      return parsed !== undefined && formatIso(parsed) === iso ? cur : iso;
    });
  }, [iso]);
  const calendarRef = React.useRef<HTMLDivElement>(null);

  if (inline) {
    return (
      <CalendarPane
        {...(value !== undefined ? { value } : {})}
        {...(onChange ? { onChange } : {})}
        dir={dir}
        hideHijri={hideHijri}
        captionLayout={captionLayout}
        {...(className ? { className } : {})}
        {...dpRest}
      />
    );
  }

  // Blur / Enter: a lenient date commits; anything else snaps back to the
  // picked date — half-typed text never survives.
  const commitText = (): void => {
    if (text.trim() === "") return;
    const parsed = parseTypedDate(text);
    if (parsed === undefined) {
      setText(iso);
      return;
    }
    setText(formatIso(parsed));
    if (formatIso(parsed) !== iso) onChange?.(parsed);
  };

  const showClear = clearable && value !== undefined && disabled !== true;
  // Reserved on `clearable`, never on `showClear`: the gap has to be held open
  // whether or not a date is currently picked, or the text jumps sideways the
  // moment one is.
  //
  // Which physical side that is takes reading twice. The button is placed with
  // `end-*` against the wrapper, which inherits the document direction — so it
  // sits on the left in an Arabic shell. The trigger is `bidi="ltr"`, and
  // `padding-inline-end` resolves against the ELEMENT's own direction, so `pe-`
  // on it is always the right. In RTL the two would land on opposite sides and
  // the X would cover the start of the date. Pad the start instead there.
  const clearInset = !clearable ? "" : dir === "rtl" ? "ps-xl" : "pe-xl";

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      {/* Anchors the clear control as a SIBLING of the trigger — and absolute,
          so the trigger keeps its full width in both states. */}
      <div className="relative">
        <PopoverTrigger asChild>
          <Input
            ref={ref}
            // A text field that opens a calendar: the trigger's aria-expanded is only
            // allowed on a combobox, not on a plain textbox.
            role="combobox"
            bidi="ltr"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={text}
            placeholder={placeholder}
            invalid={isInvalid}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(name !== undefined ? { name } : {})}
            {...(id !== undefined ? { id } : {})}
            {...(describedBy !== undefined ? { "aria-describedby": describedBy } : {})}
            {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
            className={cn(clearInset, triggerClassName)}
            onClick={() => {
              if (!disabled) setOpen((o) => !o);
            }}
            onChange={(e) => {
              const next = e.target.value;
              setText(next);
              // A full `YYYY-MM-DD` commits as it is typed; an emptied field
              // clears. Shorter forms wait for blur or Enter — committing
              // "2027-06-3" as the 3rd would fire twice on the way to the 30th.
              if (next.trim() === "") {
                if (value !== undefined) onChange?.(undefined);
                return;
              }
              const parsed = /^\d{4}-\d{2}-\d{2}$/.test(next.trim())
                ? parseTypedDate(next)
                : undefined;
              if (parsed !== undefined && formatIso(parsed) !== iso) onChange?.(parsed);
            }}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Enter commits (or snaps back) and closes the calendar without
                // submitting an enclosing form.
                e.preventDefault();
                commitText();
                setOpen(false);
              } else if (e.key === "ArrowDown") {
                // Focus stays in the input while the calendar is open (so one
                // can keep typing); ArrowDown hands it to the calendar.
                e.preventDefault();
                if (!open) setOpen(true);
                const target =
                  calendarRef.current?.querySelector<HTMLElement>(
                    '.rdp-day_button[tabindex="0"]',
                  ) ?? calendarRef.current?.querySelector<HTMLElement>("button, select");
                target?.focus();
              }
            }}
          />
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            {...(clearLabel !== undefined ? { "aria-label": clearLabel } : {})}
            onClick={() => onChange?.(undefined)}
            className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-sm p-xs text-muted hover:text-default focus-visible:focus-ring"
          >
            <Icon as={X} size={14} aria-label="" />
          </button>
        )}
      </div>
      <PopoverContent
        className="p-0 w-auto"
        align="start"
        // Opening must not move focus out of the input: the field is typeable,
        // and yanking focus mid-keystroke sent the digits into the calendar.
        // Escape is scoped by Radix's layer stack — the calendar is the
        // topmost dismissable layer, so it alone closes; an enclosing Dialog
        // never sees the key.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div ref={calendarRef}>
          <CalendarPane
            {...(value !== undefined ? { value } : {})}
            onChange={(d) => {
              onChange?.(d);
              setOpen(false);
            }}
            dir={dir}
            hideHijri={hideHijri}
            captionLayout={captionLayout}
            {...(className ? { className } : {})}
            {...paneRest}
          />
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
});
